import Link from 'next/link'
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
  Controller,
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormSetError,
  UseFormGetValues,
} from 'react-hook-form'
import clsx from 'clsx'

import ChangePhone from '../change-phone/change-phone'
import ChangeEmail from '../change-email/change-email'
import NewCodeBtn from '../new-code-btn/new-code-btn'
import EnterEmail from '../enter-email-btn/enter-email-btn'
import CountDown from 'components/parts/countdown/countdown'
import EnterPhoneBtn from '../enter-phone-btn/enter-phone-btn'
import Auth from 'store/auth'
import useActivePopupStatus from 'store/common/useActivePopupStatus'

import numericToWord from 'utils/numericToWord'
import { concatParams, cutHashFromURL, getParams } from 'utils/getURLData'
import { FormValues } from '../../data'
import { FieldsNameVoc } from 'constants/auth/profile-data'
import { nestApiPath, profileURL } from 'constants/url'
import { AUTH_URL, MAIN_PAGE_URL, PROMO_REDIRECT_URL } from 'constants/data'
import { getRequest, postAxiosSingle, setToken } from 'pages/api/axios'
import { RegStep, FieldCode, FieldId } from '../../data'
import { genCodeErrorEnum, ATTEMPTS_WORD_FORM } from './data'
import { isAdAgree } from '../../data'
import {
  A_signinProgress,
  A_signinErrors,
  A_regFinish,
  stepRegProgress,
  stepErrors,
  succesSignin,
  A_signinSucces,
} from 'utils/analytics/other/registration/registration'

import styles from './final-widget.module.scss'
import common from '../../registration-code.module.scss'

interface IFinalWidget {
  control: Control<FormValues>
  errors: FieldErrors
  getValues: UseFormGetValues<FormValues>
  setError: UseFormSetError<FormValues>
  setValue: UseFormSetValue<FormValues>
  setStep: React.Dispatch<React.SetStateAction<RegStep>>
  theme?: 'phone' | 'email'
}

const getPath = (path: string) => {
  const params = getParams(path)
  const formedParams = concatParams(params)
  const completePath = formedParams ? `${MAIN_PAGE_URL}?${formedParams}` : MAIN_PAGE_URL
  return completePath
}

const FinalWidget: FC<IFinalWidget> = ({
  control,
  errors,
  setStep,
  setValue,
  getValues,
  setError,
  theme = 'phone',
}) => {
  const [inputValue, setIInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [isEndTime, setEnd] = useState(false)
  const chartRef = useRef<any>()
  const coutDownReset = useRef<() => void>(() => null)
  const coutDownToggle = useRef<() => void>(() => null)
  const userInfo = getValues()
  const isRussia = userInfo[FieldsNameVoc.countryCode] === 'RU'
  const isAd = userInfo[isAdAgree]
  const phone = userInfo[FieldsNameVoc.phone]?.replace(/\D/g, '')
  const email = userInfo[FieldsNameVoc.email]
  const speciality: { value: number }[] | null = userInfo[FieldsNameVoc.Specialities]
  const idUser = userInfo[FieldId]
  const isPhoneTheme = theme === 'phone'
  const formatedContact = isPhoneTheme ? phone : email

  const { setAuth } = Auth.useHandleAuth()
  const { setHashPopupStatus } = useActivePopupStatus()
  const router = useRouter()

  const loginV2 = async (currentCode: string) => {
    const path = cutHashFromURL(router.asPath)

    const valuesToSend: any = {
      contact: formatedContact,
      code: currentCode,
      id: idUser,
      channel: theme,
    }

    if (speciality) {
      const form = {
        phone,
        email,
        isAdAgree: userInfo.isAdAgree,
        path: cutHashFromURL(window.location.href),
        [FieldsNameVoc.Specialities]: speciality.map((s) => s.value),
      }
      valuesToSend.form = form
    }
    const userData = await postAxiosSingle(`${AUTH_URL}/v2/login`, {}, valuesToSend)
    if (userData.status === 200) {
      const { accessToken, expireTime, currentTime } = userData.data
      setToken(accessToken)
      const currentProfile = await getRequest(profileURL)

      setAuth({
        userProfile: currentProfile.data,
        accessToken,
        expireTime,
        currentTime,
      })
      isRussia ? A_signinSucces(theme as succesSignin) : A_signinSucces(succesSignin.FOREIGN)
      A_regFinish(path, isAd)

      if (idUser) {
        const url = path === PROMO_REDIRECT_URL ? getPath(path) : path
        router.replace(url, undefined, { shallow: true })
        setHashPopupStatus()
      } else {
        setStep(RegStep.DOP_INPUTS)
      }
    }

    if (userData?.response?.status === 400) {
      const formatedData = JSON.parse(userData.response.data.status)
      if (formatedData?.reason === 'Wrong code') {
        setError(FieldCode, {
          type: 'custom',
          message: `Неверный код, осталось ${formatedData.attemptsLeft} ${numericToWord(formatedData.attemptsLeft, ATTEMPTS_WORD_FORM)}`,
        })
        A_signinErrors(stepErrors.WRONG_CODE, theme)

        if (Number(formatedData?.attemptsLeft) === 0) {
          isPhoneTheme ? setStep(RegStep.NO_ATTEMPTS_PHONE) : setStep(RegStep.NO_ATTEMPTS_EMAIL)
          A_signinErrors(stepErrors.LIMITS_CODE, theme)
        }
        setIInputValue('')
        setValue(FieldCode, '')
      } else {
        setStep(RegStep.ERROR)
      }
    }
    if (userData?.response?.status === 409) {
      setStep(RegStep.ERROR)
    }
    if (userData?.response?.status === 500) {
      setStep(RegStep.ERROR)
    }
  }

  const codeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const userCode = event.target.value
    setIInputValue(userCode)
    setError(FieldCode, { type: 'custom', message: '' })
    if (userCode.length >= 4) loginV2(userCode)
  }

  const endTimeHandler = () => {
    setEnd(true)
  }

  const getNewCode = () => {
    if (isEndTime) {
      setEnd(false)
      setError(FieldCode, { type: 'custom', message: '' })
      getCode()
      A_signinErrors(stepErrors.REQUEST_CODE, theme)
    }
  }

  const zoneFocus = () => {
    if (chartRef.current) {
      chartRef.current.focus()
      setError(FieldCode, { type: 'custom', message: '' })
    }
  }

  const getCode = async () => {
    setEnd(false)
    const resultCode = await postAxiosSingle(
      `${nestApiPath}/user/generateaccesscode`,
      {},
      {
        contact: formatedContact,
        channel: theme,
        id: idUser,
      }
    )
    if (resultCode.status === 201) {
      setValue(FieldCode, resultCode.data.code)
      coutDownReset.current()
    }
    if (resultCode?.response?.status === 429) {
      if (resultCode?.response?.data.message === genCodeErrorEnum.DAY_LIMIT) {
        isPhoneTheme ? setStep(RegStep.NO_ATTEMPTS_PHONE) : setStep(RegStep.NO_ATTEMPTS_EMAIL)
        A_signinErrors(stepErrors.LIMITS_CODE, theme)
        return
      }
      if (resultCode?.response?.data.message === genCodeErrorEnum.HOURE_LIMIT) {
        isPhoneTheme ? setStep(RegStep.NO_ATTEMPTS_PHONE) : setStep(RegStep.NO_ATTEMPTS_EMAIL)
        A_signinErrors(stepErrors.LIMITS_CODE, theme)
        return
      }
      setIInputValue('')
      coutDownToggle.current()
    }
  }

  useEffect(() => {
    if (isRussia) {
      isPhoneTheme
        ? A_signinProgress(stepRegProgress.PHONE)
        : A_signinProgress(stepRegProgress.EMAIL)
    }
    getCode()
  }, [theme])

  return (
    <>
      <div className={common.wrapperCommon}>
        <div className={common.title} onClick={() => coutDownToggle.current()}>
          {isPhoneTheme ? 'Введите последние 4 цифры входящего номера' : 'Введите код из письма'}
        </div>

        <div className={clsx(styles.phoneWrapper, common.subtitle)}>
          <div>
            {isPhoneTheme ? (
              <p>
                Мы звоним на номер <span>+{phone}</span>
              </p>
            ) : (
              <p>
                Вам должно поступить письмо с 4-х значным кодом на адрес <span>{email}</span>
                {!isPhoneTheme && email && <ChangeEmail setStep={setStep} />}
              </p>
            )}
          </div>
          {isPhoneTheme && <ChangePhone setStep={setStep} />}
        </div>
        <div className={styles.hidenInput} onClick={zoneFocus}>
          <Controller
            control={control}
            name={FieldCode}
            render={({ field: { onBlur } }) => {
              return (
                <input
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    codeHandler(e)
                  }}
                  onBlur={() => {
                    onBlur()
                    setIsFocused(false)
                  }}
                  onFocus={() => {
                    setIsFocused(true)
                  }}
                  type="number"
                  value={inputValue}
                  ref={chartRef}
                ></input>
              )
            }}
          />
        </div>
        <div className={styles.codeZone}>
          {Array.from(Array(4).keys()).map((index) => {
            return (
              <div
                key={index}
                className={clsx(styles.valueZone, {
                  [styles.zoneActive]: isFocused && inputValue.length === index,
                  [styles.zoneError]: errors[FieldCode]?.message,
                })}
              >
                {inputValue[index]}
              </div>
            )
          })}
        </div>
        {errors[FieldCode] && <div className={styles.error}>{errors[FieldCode]?.message}</div>}
        {!isEndTime && (
          <div className={styles.countDownWrap}>
            Получить новый {isPhoneTheme ? 'звонок' : 'код'} можно через
            <CountDown
              hours={0}
              minutes={1}
              seconds={0}
              refToggle={coutDownToggle}
              refReset={coutDownReset}
              onEnd={endTimeHandler}
            />
          </div>
        )}
        <div className={styles.btnBottom}>
          {isEndTime && <NewCodeBtn onClick={getNewCode} />}
          {isPhoneTheme ? (
            <EnterEmail setStep={setStep} getValues={getValues} />
          ) : (
            <EnterPhoneBtn setStep={setStep} />
          )}
        </div>
        {!isPhoneTheme && (
          <div className={styles.emailInfo}>
            Если вы не получили письмо, пожалуйста, напишите на почту{' '}
            <Link href="mailto:info@medpoint.pro">
              <span className={common.link}>info@medpoint.pro</span>
            </Link>
          </div>
        )}
      </div>
    </>
  )
}

export default FinalWidget
